import { noop } from '@ewn/fp';
import { computed, getCurrentInstance, onMounted, onUnmounted, readonly, ref } from 'vue-demi';

/**
 * Represents the position of overflow for an HTMLElement.
 * @readonly
 * @enum {string}
 */
export const OverflowPosition = Object.freeze({
  /** The element is not overflowing. */
  none: Symbol('none'),
  /** The element is overflowing to the left or top. */
  start: Symbol('start'),
  /** The element is overflowing to the right or bottom. */
  end: Symbol('end'),
  /** The element is overflowing at both sides of the axis. */
  both: Symbol('both')
});

function watchEvent (el, type, listener) {
  el.addEventListener(type, listener);
  return () => el.removeEventListener(type, listener);
}

function getOverflow (clientDim, scrollDim, scrollPos) {
  if (scrollDim <= clientDim) {
    return OverflowPosition.none;
  }
  if (scrollPos === 0) {
    return OverflowPosition.start;
  }
  if (scrollPos >= scrollDim - clientDim) {
    return OverflowPosition.end;
  }
  return OverflowPosition.both;
}

function getOverflowX (el) {
  return getOverflow(el.clientWidth, el.scrollWidth, el.scrollLeft);
}

function getOverflowY (el) {
  return getOverflow(el.clientHeight, el.scrollHeight, el.scrollTop);
}

function toggleClasses (el, position, dimClass, startClass, endClass) {
  switch (position) {
    case OverflowPosition.none:
      el.classList.remove(dimClass, startClass, endClass);
      break;
    case OverflowPosition.start:
      el.classList.remove(startClass);
      el.classList.add(dimClass, endClass);
      break;
    case OverflowPosition.end:
      el.classList.remove(endClass);
      el.classList.add(dimClass, startClass);
      break;
    case OverflowPosition.both:
      el.classList.add(dimClass, startClass, endClass);
      break;
  }
}

function checkOverflow (scrollable, classTarget, syncClasses, overflowFn, position, dimClass, startClass, endClass) {
  const pos = overflowFn(scrollable);
  if (pos === position.value) return;
  if (syncClasses) {
    toggleClasses(classTarget, pos, dimClass, startClass, endClass);
  }
  position.value = pos;
}

/**
 * @typedef {Object} OverflowOptions
 * @property {string} overflowXClass - The class to use for overflow on the X-axis (default: `overflow-x`).
 * @property {string} overflowYClass - The class to use for overflow on the Y-axis (default: `overflow-y`).
 * @property {string} overflowLeftClass - The class to use for overflow on the left (default: `overflow-left`).
 * @property {string} overflowTopClass - The class to use for overflow on the top (default: `overflow-top`).
 * @property {string} overflowRightClass - The class to use for overflow on the right (default: `overflow-right`).
 * @property {string} overflowBottomClass - The class to use for overflow on the bottom (default: `overflow-bottom`).
 * @property {Function} scrollableFn - A function to retrieve the scrollable element.
 * @property {Function} classTargetFn - A function to retrieve the element to which overflow classes will be applied.
 * @property {Boolean} watchOnMounted - Indicates if the overflow should be watched automatically when the component is mounted.
 * @property {Boolean} syncClasses - Indicates if overflow classes should automatically be synced to the class target element.
 */

/**
 * @typedef {Object} OverflowApi
 * @property {ComputedRef<Boolean>} isOverflowing - Indicates if the element is overflowing.
 * @property {readonly<Ref<OverflowPosition>>} overflowX - The overflow position on the X-axis.
 * @property {readonly<Ref<OverflowPosition>>} overflowY - The overflow position on the Y-axis.
 * @property {Function} watchOverflow - Starts watching for overflow on the element.
 * @property {Function} unwatchOverflow - Stops watching for overflow on the element.
 */

/**
 * Reactive wrapper for HTMLElement overflow.
 * @param {OverflowOptions} options - The options for configuring overflow.
 * @return {OverflowApi} The overflow API.
 */
export function useOverflow (options = {}) {
  const {
    overflowXClass = 'overflow-x',
    overflowYClass = 'overflow-y',
    overflowLeftClass = 'overflow-left',
    overflowTopClass = 'overflow-top',
    overflowRightClass = 'overflow-right',
    overflowBottomClass = 'overflow-bottom',
    scrollableFn,
    classTargetFn = scrollableFn,
    watchOnMounted = true,
    syncClasses = true
  } = options;

  let unwatch = noop;
  const overflowX = ref(OverflowPosition.none);
  const overflowY = ref(OverflowPosition.none);

  function checkOverflows (scrollable, classTarget) {
    checkOverflow(scrollable, classTarget, syncClasses, getOverflowX, overflowX,
      overflowXClass, overflowLeftClass, overflowRightClass);
    checkOverflow(scrollable, classTarget, syncClasses, getOverflowY, overflowY,
      overflowYClass, overflowTopClass, overflowBottomClass);
  }

  function watch (scrollable, classTarget = scrollable) {
    unwatch();
    const check = checkOverflows.bind(null, scrollable, classTarget);
    check();
    const unwatchWindowResize = watchEvent(window, 'resize', check);
    const unwatchResize = watchEvent(scrollable, 'resize', check);
    const unwatchScroll = watchEvent(scrollable, 'scroll', check);
    unwatch = () => {
      unwatchWindowResize();
      unwatchResize();
      unwatchScroll();
      if (syncClasses) {
        classTarget.classList.remove(
          overflowXClass, overflowYClass, overflowLeftClass,
          overflowTopClass, overflowRightClass, overflowBottomClass);
      }
    };
  }

  function watchOverflow () {
    watch(scrollableFn(), classTargetFn());
  }

  function unwatchOverflow () {
    unwatch();
  }

  if (getCurrentInstance()) {
    if (watchOnMounted) {
      onMounted(() => watchOverflow());
    }
    onUnmounted(() => unwatchOverflow());
  }

  return {
    isOverflowing: computed(() =>
      overflowX.value !== OverflowPosition.none ||
      overflowY.value !== OverflowPosition.none),
    overflowX: readonly(overflowX),
    overflowY: readonly(overflowY),
    watchOverflow,
    unwatchOverflow
  };
}
